import { useQuery } from "@tanstack/react-query";
import { type DateTime } from "luxon";

import { getMachinesInTimeRange } from "@/api/reporting-api";
import type { PlannedProductionData } from "@/schemas";

import { getReportingGcTime, getReportingStaleTime } from "./utils";
import { REPORTING_QUERY_KEYS } from "./query-keys";

export type MachineSelectionQueryParams = {
	schedules: PlannedProductionData[];
	sites: string[];
	startDate: DateTime;
	endDate: DateTime;
	timeZoneId: string;
};

const isValidMachineSelectionParams = (params: MachineSelectionQueryParams): boolean => {
	return Boolean(params.sites?.length && params.startDate?.isValid && params.endDate?.isValid);
};

export const useMachineSelection = (params: MachineSelectionQueryParams, options: { enabled?: boolean } = {}) => {
	const isValidParams = isValidMachineSelectionParams(params);
	const isEnabled = isValidParams && (options.enabled ?? true);

	return useQuery({
		queryKey: REPORTING_QUERY_KEYS.machineSelection(params),
		queryFn: ({ signal }) =>
			getMachinesInTimeRange(
				params.schedules,
				params.sites,
				params.startDate,
				params.endDate,
				params.timeZoneId,
				signal,
			),
		staleTime: getReportingStaleTime([params.startDate, params.endDate]),
		gcTime: getReportingGcTime([params.startDate, params.endDate]),
		enabled: isEnabled,
	});
};
